import { useContext, useMemo, Key } from 'react';
import { useFormContext } from 'react-hook-form';
import { orderBy } from 'lodash-es';
import { AuthContext } from '@lp/core/auth';
import { DISABLED_KEYS_BY_ROLES, EMPLOYEE_ROLES_SELECT_LIST } from '@lp/features/admin/forms/EmployeeForm/config';
import { EmployeeFormValues } from '@/types';
import { EmployeeRole } from '@/api/apis/resources';
import { AdminPermissions } from '@/permissions';
import { MultiSelectWithChipsRow } from '../MultiSelectWithChipsRow';
import { getRoleCleanupRules } from './utils';

interface EmployeeRoleSelectorProps {
  isReadOnly?: boolean;
}

const rolesSelectList = orderBy(
  EMPLOYEE_ROLES_SELECT_LIST.filter((role) => role.value !== EmployeeRole.ContractorRetrievalSurgeon),
  'name',
);

export const EmployeeRoleSelector = ({ isReadOnly }: EmployeeRoleSelectorProps) => {
  const { roles: currentRoles, checkPermission } = useContext(AuthContext);
  const { setValue, getValues } = useFormContext<EmployeeFormValues>();

  const hasEmployeeRolesEditPermission = checkPermission(AdminPermissions.TmdxUsersUpdateRoles);

  const disabledRolesKeys = useMemo(() => {
    const currentRole = Object.keys(DISABLED_KEYS_BY_ROLES).find((r) =>
      currentRoles.includes(r as EmployeeRole),
    ) as EmployeeRole;

    return DISABLED_KEYS_BY_ROLES[currentRole] || [];
  }, [currentRoles]);

  const onRolesChange = (keys: Key[]) => {
    const selectedRoles = keys.map((key) => String(key) as EmployeeRole);
    const cleanupRules = getRoleCleanupRules(selectedRoles, getValues());

    cleanupRules.forEach((rule) => {
      if (rule.condition) {
        Object.entries(rule.fields).forEach(([fieldName, value]) => {
          setValue(fieldName as keyof EmployeeFormValues, value);
        });
      }
    });
  };

  return (
    <MultiSelectWithChipsRow
      name="roles"
      title="Role"
      isRequired
      ariaLabel="Role"
      placeholder="Select a role"
      items={rolesSelectList}
      isDisabled={isReadOnly || !hasEmployeeRolesEditPermission}
      disabledKeys={disabledRolesKeys}
      onSelectionChangeExtra={onRolesChange}
    />
  );
};
